import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Users,
  DollarSign,
  TrendingUp,
  Wallet,
  Gift,
  ArrowUp,
  ArrowDown,
  Calendar,
  Loader2,
  BarChart3,
  PieChart,
} from "lucide-react";

const AdminReferralAnalytics = () => {
  const { token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30");
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/api/referrals/admin/analytics?days=${timeRange}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        throw new Error("Failed to fetch analytics");
      }
    } catch (error) {
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPercentChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-rnb-blue" />
        </div>
      </AdminLayout>
    );
  }

  const stats = analytics?.stats || {};
  const topPartners = analytics?.top_partners || [];
  const programBreakdown = analytics?.program_breakdown || [];
  const monthlyTrend = analytics?.monthly_trend || [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy" data-testid="referral-analytics-title">
              Referral Analytics
            </h1>
            <p className="text-rnb-gray">Track referral program performance and ROI</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[150px]" data-testid="time-range-select">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAnalytics}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-modern" data-testid="metric-total-referrals">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-rnb-blue/10">
                  <Users className="h-5 w-5 text-rnb-blue" />
                </div>
                {stats.referrals_change !== undefined && (
                  <div className={`flex items-center gap-1 text-sm ${
                    stats.referrals_change >= 0 ? "text-green-600" : "text-red-600"
                  }`}>
                    {stats.referrals_change >= 0 ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )}
                    {Math.abs(stats.referrals_change)}%
                  </div>
                )}
              </div>
              <p className="text-3xl font-bold text-rnb-navy">{stats.total_referrals || 0}</p>
              <p className="text-sm text-rnb-gray">Total Referrals</p>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="metric-conversion-rate">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-green-100">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-rnb-navy">
                {stats.conversion_rate?.toFixed(1) || 0}%
              </p>
              <p className="text-sm text-rnb-gray">Conversion Rate</p>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="metric-total-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-rnb-teal/10">
                  <DollarSign className="h-5 w-5 text-rnb-teal" />
                </div>
                {stats.revenue_change !== undefined && (
                  <div className={`flex items-center gap-1 text-sm ${
                    stats.revenue_change >= 0 ? "text-green-600" : "text-red-600"
                  }`}>
                    {stats.revenue_change >= 0 ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )}
                    {Math.abs(stats.revenue_change)}%
                  </div>
                )}
              </div>
              <p className="text-3xl font-bold text-rnb-navy">
                {formatCurrency(stats.total_revenue || 0)}
              </p>
              <p className="text-sm text-rnb-gray">Revenue from Referrals</p>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="metric-total-commissions">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Wallet className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-rnb-navy">
                {formatCurrency(stats.total_commissions || 0)}
              </p>
              <p className="text-sm text-rnb-gray">Commissions Paid</p>
            </CardContent>
          </Card>
        </div>

        {/* ROI Card */}
        <Card className="card-modern bg-gradient-to-r from-rnb-navy to-rnb-blue text-white">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-white/70 text-sm mb-1">Return on Investment (ROI)</p>
                <p className="text-4xl font-bold">
                  {stats.roi?.toFixed(0) || 0}%
                </p>
                <p className="text-white/70 text-sm mt-1">
                  For every $1 spent on commissions, you earned ${((stats.roi || 0) / 100 + 1).toFixed(2)} in revenue
                </p>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.active_partners || 0}</p>
                  <p className="text-white/70 text-sm">Active Partners</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.avg_referrals_per_partner?.toFixed(1) || 0}</p>
                  <p className="text-white/70 text-sm">Avg. Referrals/Partner</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Partners */}
          <Card className="card-modern">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-rnb-navy flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Top Partners
              </CardTitle>
              <CardDescription>Partners with most confirmed referrals</CardDescription>
            </CardHeader>
            <CardContent>
              {topPartners.length === 0 ? (
                <div className="text-center py-8 text-rnb-gray">
                  <Gift className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No referral data yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {topPartners.slice(0, 5).map((partner, index) => (
                    <div key={partner.partner_id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? "bg-yellow-100 text-yellow-700" :
                        index === 1 ? "bg-gray-100 text-gray-700" :
                        index === 2 ? "bg-amber-100 text-amber-700" :
                        "bg-rnb-light text-rnb-gray"
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-rnb-navy truncate">{partner.name}</p>
                        <p className="text-sm text-rnb-gray">{partner.referrals} referrals</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrency(partner.earned)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Program Breakdown */}
          <Card className="card-modern">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-rnb-navy flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Program Breakdown
              </CardTitle>
              <CardDescription>Referrals by program type</CardDescription>
            </CardHeader>
            <CardContent>
              {programBreakdown.length === 0 ? (
                <div className="text-center py-8 text-rnb-gray">
                  <PieChart className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No program data yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {programBreakdown.map((program) => (
                    <div key={program.program} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-rnb-navy">
                          {program.program === "carerevive" ? "CareRevive" :
                           program.program === "foundations_first" ? "Foundations First" :
                           program.program}
                        </span>
                        <span className="text-rnb-gray">{program.count} referrals</span>
                      </div>
                      <div className="h-2 bg-rnb-light rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            program.program === "carerevive" ? "bg-rnb-blue" : "bg-rnb-teal"
                          }`}
                          style={{ width: `${program.percentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-rnb-gray">{program.percentage.toFixed(1)}%</span>
                        <span className="text-green-600 font-medium">{formatCurrency(program.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend */}
        <Card className="card-modern">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-rnb-navy">Monthly Trend</CardTitle>
            <CardDescription>Referrals and revenue over time</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyTrend.length === 0 ? (
              <div className="text-center py-8 text-rnb-gray">
                <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No trend data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {monthlyTrend.map((month) => (
                  <div key={month.month} className="flex items-center gap-4 p-3 bg-rnb-light rounded-lg">
                    <div className="w-24 font-medium text-rnb-navy">{month.month}</div>
                    <div className="flex-1 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-rnb-blue" />
                        <span className="text-sm">{month.referrals} referrals</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-600 font-medium">
                          {formatCurrency(month.revenue)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminReferralAnalytics;
